import React, { useState, useEffect } from 'react';
import { adminGetTierLimits, adminUpdateTierLimit } from '../../services/api';

function TierLimitsMatrix() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState({});
  const [editingValue, setEditingValue] = useState({});

  useEffect(() => {
    loadLimits();
  }, []);

  const loadLimits = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await adminGetTierLimits();
      setData(response);
    } catch (err) {
      setError('Failed to load tier limits. ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleLimitChange = (tierId, limitType, value) => {
    const key = `${tierId}-${limitType}`;
    setEditingValue(prev => ({ ...prev, [key]: value }));
  };

  const handleLimitBlur = async (tierId, limitType) => {
    const key = `${tierId}-${limitType}`;
    const newValue = editingValue[key];

    // If no change was made, skip
    if (newValue === undefined) return;

    // Parse the value
    let parsedValue;
    if (newValue === '' || newValue === '-1' || newValue.toLowerCase() === 'unlimited') {
      parsedValue = -1;
    } else {
      parsedValue = parseInt(newValue, 10);
      if (isNaN(parsedValue) || parsedValue < -1) {
        setError('Invalid value. Enter a positive number or -1 for unlimited.');
        return;
      }
    }

    // Check if value actually changed
    const currentValue = data.limits[tierId]?.[limitType];
    if (parsedValue === currentValue) {
      setEditingValue(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
      return;
    }

    setUpdating(prev => ({ ...prev, [key]: true }));
    setError('');

    try {
      await adminUpdateTierLimit(tierId, limitType, parsedValue);
      // Update local state
      setData(prev => ({
        ...prev,
        limits: {
          ...prev.limits,
          [tierId]: {
            ...prev.limits[tierId],
            [limitType]: parsedValue
          }
        }
      }));
      // Clear editing state
      setEditingValue(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
    } catch (err) {
      setError('Failed to update limit: ' + (err.response?.data?.error || err.message));
    } finally {
      setUpdating(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleKeyDown = (e, tierId, limitType) => {
    if (e.key === 'Enter') {
      e.target.blur();
    } else if (e.key === 'Escape') {
      const key = `${tierId}-${limitType}`;
      setEditingValue(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
    }
  };

  const formatLimitValue = (value) => {
    if (value === -1) return 'Unlimited';
    return value?.toLocaleString() || '0';
  };

  const getLimitDescription = (limitType) => {
    const descriptions = {
      'monthly_screener_contracts': 'Contracts returned from screener runs per month',
      'monthly_watchlist_contracts': 'Contracts simulated in watchlist per month',
      'max_saved_contracts': 'Maximum saved contracts at any time',
      'max_saved_parameters': 'Maximum saved screener parameters'
    };
    return descriptions[limitType] || '';
  };

  const getLimitDisplayName = (limitType) => {
    const names = {
      'monthly_screener_contracts': 'Screener Contracts/Mo',
      'monthly_watchlist_contracts': 'Watchlist Contracts/Mo',
      'max_saved_contracts': 'Saved Contracts (Max)',
      'max_saved_parameters': 'Saved Parameters (Max)'
    };
    return names[limitType] || limitType;
  };

  if (loading) {
    return (
      <div className="roles-loading">
        <div className="spinner"></div>
        <p>Loading tier limits...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="error-message">
        Failed to load tier limits.
        <button onClick={loadLimits} className="retry-btn">Retry</button>
      </div>
    );
  }

  return (
    <div className="tier-limits-matrix">
      <div className="matrix-header">
        <h3>Tier Limits Configuration</h3>
        <p className="matrix-subtitle">
          Configure usage limits for each subscription tier.
          Use -1 or leave blank for unlimited. Admin users bypass all limits.
        </p>
      </div>

      {error && (
        <div className="error-message" style={{ marginBottom: '1rem' }}>
          {error}
          <button onClick={() => setError('')} className="dismiss-btn">Dismiss</button>
        </div>
      )}

      <div className="matrix-container">
        <table className="feature-matrix-table tier-limits-table">
          <thead>
            <tr>
              <th className="feature-header">Limit Type</th>
              {data.tiers.map(tier => (
                <th key={tier.id} className="tier-header">
                  <span className={`tier-name tier-${tier.name}`}>
                    {tier.display_name}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.limit_types.map(limitType => (
              <tr key={limitType.code}>
                <td className="feature-cell">
                  <div className="feature-info">
                    <span className="feature-name">{getLimitDisplayName(limitType.code)}</span>
                    <span className="feature-description">
                      {getLimitDescription(limitType.code)}
                    </span>
                  </div>
                </td>
                {data.tiers.map(tier => {
                  const key = `${tier.id}-${limitType.code}`;
                  const currentValue = data.limits[tier.id]?.[limitType.code] ?? 0;
                  const isUpdating = updating[key];
                  const editValue = editingValue[key];
                  const displayValue = editValue !== undefined
                    ? editValue
                    : (currentValue === -1 ? '' : currentValue);

                  return (
                    <td key={tier.id} className="limit-cell">
                      <div className={`limit-input-wrapper ${isUpdating ? 'updating' : ''}`}>
                        <input
                          type="text"
                          className="limit-input"
                          value={displayValue}
                          onChange={(e) => handleLimitChange(tier.id, limitType.code, e.target.value)}
                          onBlur={() => handleLimitBlur(tier.id, limitType.code)}
                          onKeyDown={(e) => handleKeyDown(e, tier.id, limitType.code)}
                          disabled={isUpdating}
                          placeholder="Unlimited"
                        />
                        {currentValue === -1 && editValue === undefined && (
                          <span className="unlimited-badge">Unlimited</span>
                        )}
                        {isUpdating && <span className="updating-spinner"></span>}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="matrix-footer">
        <div className="matrix-legend">
          <span className="legend-item">
            <strong>-1</strong> or <strong>blank</strong> = Unlimited
          </span>
          <span className="legend-item">
            Press <strong>Enter</strong> to save, <strong>Esc</strong> to cancel
          </span>
        </div>
        <button onClick={loadLimits} className="refresh-btn" disabled={loading}>
          Refresh
        </button>
      </div>
    </div>
  );
}

export default TierLimitsMatrix;
